// #7 Card, #8 CardHeader, #9 Card2. The card is the comfortable surface (--card, --r-card, --shadow); Card2 is the
// nested secondary box (--card-2, --r-tile), "as card": the same slots. Tones: glow (the page's live action or a
// breach), warn, okay and bad (Blocked / failure). A card that opens something (href or onOpen) lifts on hover and its
// title is the link: a stretched link, so the whole card is the pointer target and the focus ring sits on the card.
// Slots (DS §2.1): loading → Skeleton at the final height (aria-busy); empty → EmptyState; error → ErrorStrip;
// refused → Banner tone="red" at the top, his rule sentence, entered values kept, and `newer` (a render prop) for the
// newer value and Reload that G5/G8 inject.

import { useId, type MouseEvent, type ReactNode } from "react";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { Banner } from "./Banner";
import { Count } from "./Count";
import { EmptyState } from "./EmptyState";
import { ErrorStrip, type ErrorStripProps } from "./ErrorStrip";
import { Icon, type IconName } from "./icons";
import { Skeleton } from "./Skeleton";

export type CardTone = "glow" | "warn" | "okay" | "bad";

/** The refused slot: the server said no after the act. */
export interface CardRefused {
  /** His rule sentence. */
  reason: string;
  /** A newer version: the newer value as a Diff with Reload (G5/G8 compose it). A node, or a render prop. */
  newer?: ReactNode | (() => ReactNode) | undefined;
}

/** The loading slot: skeleton lines, or one block of a given height in px (the content's final height). */
export type CardLoading = boolean | { lines?: number | undefined; height?: number | undefined };

export interface CardHeaderProps {
  /** The card title: his noun (*Coverage validator*, *Offer readiness*, *Attendance roll*). */
  title?: ReactNode | undefined;
  /** The scope word or a meta line (*Enterprise*, *Assigned*, *Own reqs*). */
  sub?: ReactNode | undefined;
  icon?: IconName | undefined;
  /** A Count after the title. */
  count?: number | undefined;
  /** Pills after the title. */
  pills?: ReactNode | undefined;
  /** Right-aligned actions. */
  actions?: ReactNode | undefined;
  /** The title element's id (a section is labelled by it). */
  id?: string | undefined;
  /** The title's heading level (default 3). */
  level?: 2 | 3 | 4 | undefined;
  className?: string | undefined;
}

/** #8: the title row — icon, title, count, pills, scope word, and actions on the right. */
export function CardHeader({ title, sub, icon, count, pills, actions, id, level = 3, className }: CardHeaderProps) {
  const Heading = `h${level}` as const;
  return (
    <div className={cx("mc-card-h", className)}>
      {icon ? <Icon name={icon} size={18} tone="muted" /> : null}
      {title !== undefined ? <Heading className="mc-ct" id={id}>{title}</Heading> : null}
      {count !== undefined ? <Count n={count} /> : null}
      {pills ?? null}
      {sub !== undefined ? <span className="mc-cs">{sub}</span> : null}
      {actions ? <div className="mc-ca">{actions}</div> : null}
    </div>
  );
}

export interface CardProps extends Omit<CardHeaderProps, "id" | "className"> {
  children?: ReactNode;
  tone?: CardTone | undefined;
  /** 12/14 padding. */
  tight?: boolean | undefined;
  /** No padding, content clipped to the radius (a table or a list edge to edge). */
  flush?: boolean | undefined;
  /** The card opens a screen: its title becomes a link covering the card, and the card lifts on hover. */
  href?: string | undefined;
  onOpen?: ((event: MouseEvent<HTMLElement>) => void) | undefined;
  loading?: CardLoading | undefined;
  /** The card has nothing: his sentence (a string is drawn as an EmptyState) or an EmptyState. */
  empty?: ReactNode | undefined;
  /** A system or integration failure: the ErrorStrip under the header. */
  error?: ErrorStripProps | undefined;
  refused?: CardRefused | undefined;
  /** false: the seat may not see this card; nothing renders. */
  can?: boolean | undefined;
  hs?: Hs | undefined;
  id?: string | undefined;
  className?: string | undefined;
}

interface SurfaceProps extends CardProps {
  kind: "card" | "card2";
}

function Surface(props: SurfaceProps) {
  const {
    kind, children, title, sub, icon, count, pills, actions, level, tone, tight, flush, href, onOpen, loading, empty, error, refused,
    can, hs, id, className,
  } = props;
  const titleId = useId();
  const hot = useHs(hs);
  if (can === false) return null;
  const opens = href !== undefined || onOpen !== undefined;
  // The stretched link carries the title's words; with no title there is nothing to name it, so the card does not open.
  const linked = opens && title !== undefined;
  const heading = linked
    ? href !== undefined
      ? <a className="mc-card-link" href={href} onClick={onOpen} {...hot}>{title}</a>
      : <button type="button" className="mc-card-link" onClick={onOpen} {...hot}>{title}</button>
    : title;
  const header = kind === "card"
    ? (heading !== undefined || sub !== undefined || icon || count !== undefined || pills || actions)
      ? <CardHeader title={heading} sub={sub} icon={icon} count={count} pills={pills} actions={actions} id={titleId} level={level} />
      : null
    : heading !== undefined || sub !== undefined || icon || count !== undefined || pills || actions
      ? (
        <div className="mc-card-2h">
          {icon ? <Icon name={icon} size={16} tone="muted" /> : null}
          {heading !== undefined ? <div className="mc-sec-t" id={titleId}>{heading}</div> : null}
          {count !== undefined ? <Count n={count} /> : null}
          {pills ?? null}
          {sub !== undefined ? <span className="mc-cs">{sub}</span> : null}
          {actions ? <div className="mc-ca">{actions}</div> : null}
        </div>
      )
      : null;
  const newer = refused?.newer === undefined ? null : typeof refused.newer === "function" ? refused.newer() : refused.newer;
  const loadingSpec = loading === true ? {} : loading || null;
  let body: ReactNode = children;
  if (loadingSpec) {
    body = loadingSpec.height !== undefined
      ? <Skeleton height={loadingSpec.height} />
      : <Skeleton lines={loadingSpec.lines ?? 3} />;
  } else if (empty !== undefined && empty !== null && empty !== false) {
    body = typeof empty === "string" ? <EmptyState>{empty}</EmptyState> : empty;
  }
  const Tag = title !== undefined ? "section" : "div";
  return (
    <Tag
      id={id}
      className={cx(
        kind === "card" ? "mc-card" : "mc-card-2", tone && `mc-card-${tone}`, tight && "mc-card-tight", flush && "mc-card-flush",
        linked && "mc-lift", className,
      )}
      aria-labelledby={title !== undefined ? titleId : undefined}
      aria-busy={loadingSpec ? true : undefined}
    >
      {refused ? (
        <Banner tone="red" live className="mc-card-refused">
          {refused.reason}
          {newer ? <div className="mc-card-newer">{newer}</div> : null}
        </Banner>
      ) : null}
      {header}
      {error ? <ErrorStrip {...error} /> : null}
      {body}
    </Tag>
  );
}

/** #7: the card. */
export function Card(props: CardProps) {
  return <Surface {...props} kind="card" />;
}

/** #9: the secondary card (box) on --card-2; its title is a section title. The same slots as Card. */
export function Card2(props: CardProps) {
  return <Surface {...props} kind="card2" />;
}
